import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import 'yup-phone';

import Button from '../../../components/shared/Button/Button';
import CustomTextField from '../../../components/shared/CustomTextField/CustomTextField';
import { createStripeUser } from '../../../redux/actions/stripeActions';
import { createExperientialRewardDetails, updateExperientialRewardDetails } from '../../../redux/actions/userActions';
import urls from '../../../routing/urls';

import 'react-phone-input-2/lib/material.css';
import styles from './ExperientialRewardForm.module.scss';

const INITIAL_VALUES = {
  name: '',
  email: '',
  street_address: '',
  unit: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  phone_number: '',
  delivery_instructions: '',
};

const PhoneNumberField = ({ setIsValidPhone, isValidPhone, errors, ...props }) => {
  useEffect(() => {
    setIsValidPhone(!errors.phone_number);
  });

  return (
    <div id={styles.PhoneField}>
      <PhoneInput
        specialLabel="Phone Number"
        country="us"
        containerClass={styles.phoneNumberContainer}
        inputClass={styles.phoneInput}
        {...props}
      />
    </div>
  );
};

const ExperientialRewardForm = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [phone, setPhone] = useState();
  const [isValidPhone, setIsValidPhone] = useState(false);

  const isPaymentInfoAdded = useSelector((state) => state.userReducer.user?.payment_info);
  const isExperientialPaymentInfoAdded = useSelector((state) => state.userReducer.user?.experiential_payment_info);
  const details = useSelector((state) => state.userReducer.expRewardDetails);

  const handleCreateSuccessCallback = useCallback(() => {
    if (isPaymentInfoAdded) navigate(urls.payments);
    else {
      dispatch(createStripeUser(() => navigate(urls.payments)));
    }
  }, [isPaymentInfoAdded]);

  const handleUpdateSuccessCallback = useCallback(() => {
    navigate(urls.paymentSettings);
  }, []);

  const validationSchema = Yup.object().shape({
    phone_number: Yup.string().required(t('validation:phone.required')),
  });

  const handleSubmit = useCallback(
    (address) => {
      const user_address = { ...address, phone_number: `+${phone}` };
      if (!isExperientialPaymentInfoAdded) {
        dispatch(createExperientialRewardDetails(user_address, handleCreateSuccessCallback));
      } else {
        dispatch(updateExperientialRewardDetails(user_address, handleUpdateSuccessCallback));
      }
    },
    [phone]
  );

  useEffect(() => {
    if (details?.phone_number) {
      setPhone(details.phone_number.replace('+', ''));
    }
  }, []);

  return (
    <div className={styles.formContainer}>
      <div className={styles.headingContent}>
        <p className={styles.heading}>{t('experientialReward.title')}</p>
        <p className={styles.subHeading}>{t('experientialReward.description')}</p>
      </div>
      <Formik
        initialValues={isExperientialPaymentInfoAdded ? details : INITIAL_VALUES}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, setFieldValue }) => {
          const disabled = !(
            values.name &&
            values.email &&
            values.street_address &&
            values.city &&
            values.state &&
            values.country &&
            values.postal_code &&
            isValidPhone
          );
          return (
            <Form>
              <div className={styles.fieldsContainer}>
                <Field name="name" label={t('formLabels.fullName')} as={CustomTextField} />
                <Field name="email" type="email" label={t('formLabels.recipientEmail')} as={CustomTextField} />
                <Field name="street_address" label={t('formLabels.streetAddress')} as={CustomTextField} />
                <Field name="unit" label={t('formLabels.unitSuite')} as={CustomTextField} />
                <div className={styles.twoFieldsContainer}>
                  <Field name="city" label={t('formLabels.city')} as={CustomTextField} />
                  <Field name="state" label={t('formLabels.stateProvince')} as={CustomTextField} />
                </div>
                <div className={styles.twoFieldsContainer}>
                  <Field name="country" label={t('formLabels.country')} as={CustomTextField} />
                  <Field name="postal_code" label={t('formLabels.postalCode')} as={CustomTextField} />
                </div>
                <PhoneNumberField
                  value={phone}
                  name="phone_number"
                  onChange={(data) => {
                    setFieldValue('phone_number', data.length > 0 ? `+${data}` : data);
                    setPhone(data);
                  }}
                  isValidPhone={isValidPhone}
                  setIsValidPhone={setIsValidPhone}
                  errors={errors}
                />
                <p className={styles.subtitle}>{t('experientialReward.phoneNote')}</p>
                <Field
                  name="delivery_instructions"
                  label={t('formLabels.additionalDeliveryInstructions')}
                  as={CustomTextField}
                />
              </div>
              <div className={styles.btnContainer}>
                <Button
                  variant="secondary-outline"
                  className={styles.backButton}
                  onClick={() => navigate(urls.paymentRecords)}
                >
                  {t('back')}
                </Button>
                <Button disabled={disabled} variant="primary" type="submit" className={styles.saveButton}>
                  {isExperientialPaymentInfoAdded ? t('save') : t('next')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ExperientialRewardForm;
